import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlay, FiVolume2, FiVolumeX, FiHeart, FiMessageCircle, FiBookmark, FiShare2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { CATEGORIES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../LoginPrompt/LoginPrompt';
import { favoritesAPI, reactionsAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import './VideoReelCard.css';

const VideoReelCard = ({ post, isVisible }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({
    like: 0,
    love: 0,
    smart: 0,
    wow: 0,
    think: 0
  });
  const [isSaved, setIsSaved] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const videoRef = useRef(null);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getVideoSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'video';
    return post?.id ? `${base}-${post.id}` : base;
  };

  const category = CATEGORIES.find(cat => cat.id === post.category);
  const posterUrl = post?.image ? resolveMediaUrl(post.image) : undefined;

  // Auto-play cuando el video es visible
  useEffect(() => {
    if (videoRef.current) {
      if (isVisible && !isPlaying) {
        videoRef.current.play();
        setIsPlaying(true);
      } else if (!isVisible && isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isVisible, isPlaying]);

  const isLoved = userReaction === 'love';
  const loveCount = useMemo(() => {
    const fromCounts = reactionCounts?.love;
    const fromReactions = post?.reactions?.love;
    const fromLegacy = post?.likes;
    if (Number.isFinite(fromCounts)) return fromCounts;
    if (Number.isFinite(fromReactions)) return fromReactions;
    return Number.isFinite(fromLegacy) ? fromLegacy : 0;
  }, [post, reactionCounts]);

  useEffect(() => {
    setReactionCounts({
      like: post?.reactions?.like ?? 0,
      love: post?.reactions?.love ?? post?.love_count ?? 0,
      smart: post?.reactions?.smart ?? 0,
      wow: post?.reactions?.wow ?? 0,
      think: post?.reactions?.think ?? 0
    });
  }, [post]);

  useEffect(() => {
    let isMounted = true;
    if (!isAuthenticated || !post?.id) return;

    (async () => {
      try {
        const [reactionRes, favRes] = await Promise.all([
          reactionsAPI.getUserReaction(post.id),
          favoritesAPI.check(post.id)
        ]);
        if (!isMounted) return;
        setUserReaction(reactionRes.data?.userReaction || reactionRes.data?.reactionType || null);
        setIsSaved(!!favRes.data?.isFavorited);
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, post?.id]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleLike = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    (async () => {
      try {
        const res = await reactionsAPI.toggle(post.id, 'love');
        setUserReaction(res.data?.userReaction || null);
        setReactionCounts(res.data?.reactions || {});
      } catch (err) {
        // ignore
      }
    })();
  };

  const handleSave = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    (async () => {
      try {
        if (isSaved) {
          const res = await favoritesAPI.remove(post.id);
          setIsSaved(!!res.data?.isFavorited);
        } else {
          const res = await favoritesAPI.add(post.id);
          setIsSaved(!!res.data?.isFavorited);
        }
      } catch (err) {
        // ignore
      }
    })();
  };

  const handleOpenReels = () => {
    // Navegar a la página de Reels con este video
    navigate(`/reels/${post.id}`);
  };

  const formatCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <>
      <article className="video-reel-card" onClick={handleOpenReels} style={{ cursor: 'pointer' }}>
      <div className="reel-container" onClick={togglePlay}>
        {post.video_url ? (
          <video
            ref={videoRef}
            src={resolveMediaUrl(post.video_url)}
            className="reel-video"
            muted={isMuted}
            loop
            playsInline
            poster={posterUrl}
            preload="metadata"
            onLoadedData={() => {
              if (!posterUrl && videoRef.current) {
                try {
                  videoRef.current.currentTime = 0.1;
                } catch (e) {
                  // ignore
                }
              }
            }}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <img
            src={resolveMediaUrl(post.image)}
            alt={post.title}
            className="reel-video"
            style={{ cursor: 'pointer' }}
          />
        )}
        
        <div className="reel-play-overlay">
          <button className="reel-play-button">
            <FiPlay />
          </button>
        </div>

        <button className="reel-mute-button" onClick={toggleMute}>
          {isMuted ? <FiVolumeX /> : <FiVolume2 />}
        </button>

        {category && (
          <span className="reel-category" style={{ background: category.color }}>
            {category.icon}
          </span>
        )}
      </div>

      <div className="reel-info">
        <div className="reel-content">
          <Link to={`/${post.author?.username || post.author?.id}`} className="reel-author">
            <img 
              src={resolveMediaUrl(post.author.avatar)} 
              alt={post.author.name}
              className="reel-author-avatar"
            />
            <span className="reel-author-name">
              {post.author.name}
              {post.author?.verification_level && post.author.verification_level !== 'none' && (
                <MdVerified
                  style={{
                    marginLeft: 6,
                    verticalAlign: 'middle',
                    color: post.author.badge_color || 'currentColor'
                  }}
                  title={post.author.badge_label || 'Cuenta verificada'}
                />
              )}
            </span>
          </Link>

          <Link to={`/video/${getVideoSlug()}`} className="reel-title">
            {post.title}
          </Link>
        </div>

        <div className="reel-actions">
          <button 
            className={`reel-action-button ${isLoved ? 'active' : ''}`}
            onClick={handleLike}
          >
            <FiHeart />
            <span>{formatCount(loveCount)}</span>
          </button>

          <Link to={`/video/${getVideoSlug()}`} className="reel-action-button">
            <FiMessageCircle />
            <span>{formatCount(post.comments)}</span>
          </Link>

          <button 
            className={`reel-action-button ${isSaved ? 'active' : ''}`}
            onClick={handleSave}
          >
            <FiBookmark />
          </button>

          <button className="reel-action-button" onClick={(e) => {
            e.stopPropagation();
            const url = `${window.location.origin}/reels/${post.id}`;
            if (navigator.share) {
              navigator.share({ title: post.title, url });
            } else {
              navigator.clipboard.writeText(url);
            }
          }}>
            <FiShare2 />
          </button>
        </div>
      </div>
    </article>

    {showLoginPrompt && (
      <LoginPrompt 
        message="Inicia sesión para interactuar con este reel"
        onClose={() => setShowLoginPrompt(false)}
      />
    )}
    </>
  );
};

export default VideoReelCard;
